/**
 * A row of bars, scaled against the tallest of them.
 *
 * Two screens draw one — sending day by day, and one campaign's send over the
 * hours it took — and both ask the same question: what shape did this have over
 * time? Written twice it was the same four decisions twice, and the second copy
 * is where one of them goes missing.
 *
 * **Relative to the busiest bar, not to an absolute scale.** The question is
 * shape, and an absolute scale flattens every small sender to a flat line.
 *
 * **A fixed track behind every bar**, so they share a baseline and an empty
 * slice is visibly empty rather than missing from the axis.
 *
 * Colour never carries meaning alone: the figures are in the title, and the
 * label under each bar says which slice it is. Roughly one man in twelve cannot
 * reliably separate red from green.
 */

/** `unknown` is its own tone, never the danger one. It may have been delivered. (I7) */
export type BarTone = 'ok' | 'warn' | 'unknown'

export interface Bar {
  readonly key: string
  /** Scaled against the largest value in the set, never against a fixed maximum. */
  readonly value: number
  readonly tone: BarTone
  /** The figures the bar is a shape of, read on hover. */
  readonly title: string
  /** Under the bar. Two or three characters — this is not a legend. */
  readonly label: string
}

const TONES: Record<BarTone, string> = {
  ok: 'bg-brand',
  warn: 'bg-warn',
  unknown: 'bg-unknown',
}

export function BarChart({ bars }: { readonly bars: readonly Bar[] }) {
  const busiest = Math.max(...bars.map((bar) => bar.value), 0)

  return (
    <div className="overflow-x-auto rounded-card border border-line bg-surface p-4">
      <div className="flex min-w-[640px] items-end gap-1">
        {bars.map((bar) => (
          <div className="flex flex-1 flex-col items-center gap-1" key={bar.key}>
            <div className="flex h-[120px] w-full flex-col justify-end rounded-field bg-line-2">
              <div
                className={`w-full rounded-field ${TONES[bar.tone]} ${HEIGHTS[height(bar.value, busiest)]}`}
                title={bar.title}
              />
            </div>
            <span className="text-[10px] text-t3">{bar.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** A tenth of the track per step, and a visible baseline for nothing at all. */
function height(value: number, busiest: number): number {
  return busiest === 0 ? 0 : Math.round((value / busiest) * 10)
}

/**
 * Literal classes, never assembled.
 *
 * Tailwind generates classes by scanning source text, so `h-[${n}px]` is a
 * class that does not exist in the stylesheet and silently renders at zero —
 * the trap `Table`'s minimum widths and `MessageFrame`'s widths both
 * document.
 */
const HEIGHTS: Record<number, string> = {
  0: 'h-[2px]',
  1: 'h-[12px]',
  2: 'h-[24px]',
  3: 'h-[36px]',
  4: 'h-[48px]',
  5: 'h-[60px]',
  6: 'h-[72px]',
  7: 'h-[84px]',
  8: 'h-[96px]',
  9: 'h-[108px]',
  10: 'h-[120px]',
}
